/* Small SVG time-series charts for the dashboards.
 *
 * Follows the dataviz house rules: one hue per series (brand accent for the
 * primary, copper for the second - never a generated palette), 2px lines
 * with a marked endpoint, thin rounded bars, a recessive dotted grid, text
 * always in text tokens (never the series colour), direct labels at the
 * line's end instead of a value on every point, and a legend only when
 * there are two series (a single series is named by the card title).
 */

const ACCENT = 'var(--accent)'
const COPPER = 'var(--copper-500)'

export interface Series { label: string; values: number[]; color?: string }

function scaleMax(all: number[]): number {
  const m = Math.max(...all, 1)
  // Headroom so the top point/bar never kisses the frame.
  return m * 1.15
}

/** "2026-W34" -> "W34" for axis ticks. */
function weekTick(w: string): string {
  const i = w.indexOf('-W')
  return i === -1 ? w : w.slice(i + 1)
}

export function WeeklyLineChart({ weeks, series }: { weeks: string[]; series: Series[] }) {
  const W = 560; const H = 180
  const padL = 8; const padR = 46; const padT = 10; const padB = 22
  const iw = W - padL - padR; const ih = H - padT - padB
  const max = scaleMax(series.flatMap((s) => s.values))
  const x = (i: number) => padL + (weeks.length < 2 ? iw / 2 : (i / (weeks.length - 1)) * iw)
  const y = (v: number) => padT + ih - (v / max) * ih
  const colors = [ACCENT, COPPER]

  return (
    <div>
      {series.length > 1 && (
        <div className="row" style={{ gap: 'var(--sp-4)', marginBottom: 'var(--sp-2)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {series.map((s, si) => (
            <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 14, height: 3, borderRadius: 2, background: s.color ?? colors[si % 2] }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
           aria-label={series.map((s) => s.label).join(' and ')}>
        {/* Recessive grid: 3 dotted horizontals, no vertical clutter. */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={padL} x2={W - padR} y1={padT + ih * f} y2={padT + ih * f}
                stroke="var(--border)" strokeWidth="1" strokeDasharray="2 4" />
        ))}
        <line x1={padL} x2={W - padR} y1={padT + ih} y2={padT + ih} stroke="var(--border)" strokeWidth="1" />
        {series.map((s, si) => {
          const color = s.color ?? colors[si % 2]
          const pts = s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')
          const last = s.values[s.values.length - 1] ?? 0
          return (
            <g key={s.label}>
              <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
              <circle cx={x(s.values.length - 1)} cy={y(last)} r="4" fill={color} />
              {/* Direct label at the line's end - value in ink, not series colour. */}
              <text x={x(s.values.length - 1) + 8} y={y(last) + 4}
                    fontSize="12" fontWeight="700" fill="var(--text)">{last}</text>
            </g>
          )
        })}
        {weeks.map((w, i) => (
          (i === 0 || i === weeks.length - 1 || i === Math.floor((weeks.length - 1) / 2)) && (
            <text key={w} x={x(i)} y={H - 6} fontSize="11" textAnchor="middle" fill="var(--text-muted)">
              {weekTick(w)}
            </text>
          )
        ))}
      </svg>
    </div>
  )
}

export function WeeklyBarChart({ weeks, values, unit = '' }: { weeks: string[]; values: number[]; unit?: string }) {
  const W = 560; const H = 180
  const padL = 8; const padR = 8; const padT = 18; const padB = 22
  const iw = W - padL - padR; const ih = H - padT - padB
  const max = scaleMax(values)
  const slot = iw / Math.max(values.length, 1)
  const bw = Math.min(24, slot * 0.55)
  const maxIdx = values.indexOf(Math.max(...values))

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="weekly totals">
      <line x1={padL} x2={W - padR} y1={padT + ih} y2={padT + ih} stroke="var(--border)" strokeWidth="1" />
      {values.map((v, i) => {
        const cx = padL + slot * i + slot / 2
        const h = (v / max) * ih
        const yTop = padT + ih - h
        // Selective direct labels: the peak and the latest week only.
        const labelled = v > 0 && (i === maxIdx || i === values.length - 1)
        return (
          <g key={i}>
            {/* Rounded data-end, square at the baseline: rounded rect clipped
                by a square-bottomed rect of half height. */}
            <rect x={cx - bw / 2} y={yTop} width={bw} height={Math.max(h, v > 0 ? 3 : 0)}
                  rx="4" fill={ACCENT} />
            {h > 6 && <rect x={cx - bw / 2} y={padT + ih - Math.min(h, 6)} width={bw} height={Math.min(h, 6)} fill={ACCENT} />}
            {labelled && (
              <text x={cx} y={yTop - 5} fontSize="12" fontWeight="700" textAnchor="middle" fill="var(--text)">
                {v}{unit}
              </text>
            )}
            {(i === 0 || i === values.length - 1 || i === Math.floor((values.length - 1) / 2)) && (
              <text x={cx} y={H - 6} fontSize="11" textAnchor="middle" fill="var(--text-muted)">
                {weekTick(weeks[i] ?? '')}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

/** Client-side 8-week bucketing for pages that already hold raw rows
 * (e.g. the admin Loads ledger) - same ISO-week convention as the
 * backend's series blocks so both chart sources read identically. */
export function bucketWeekly<T>(
  rows: T[], getIso: (r: T) => string | undefined, getValue?: (r: T) => number,
  numWeeks = 8,
): { weeks: string[]; values: number[] } {
  const weekKey = (d: Date) => {
    // ISO week: Thursday of the same week determines the year/week number.
    const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
    const day = t.getUTCDay() || 7
    t.setUTCDate(t.getUTCDate() + 4 - day)
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
    const week = Math.ceil((((t.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
    return `${t.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
  }
  const weeks: string[] = []
  const cursor = new Date()
  for (let i = 0; i < numWeeks; i++) {
    weeks.push(weekKey(cursor))
    cursor.setUTCDate(cursor.getUTCDate() - 7)
  }
  weeks.reverse()
  const bucket = new Map(weeks.map((w) => [w, 0]))
  for (const r of rows) {
    const iso = getIso(r)
    if (!iso) continue
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) continue
    const k = weekKey(d)
    if (bucket.has(k)) bucket.set(k, (bucket.get(k) ?? 0) + (getValue ? getValue(r) : 1))
  }
  return { weeks, values: weeks.map((w) => Math.round((bucket.get(w) ?? 0) * 10) / 10) }
}

/** The shared time-range selector for every trend view - mirrors the
 * reference admin panel's Today/7d/30d/... chips, expressed in the weekly
 * buckets the series actually use. */
export const CHART_RANGES = [
  { weeks: 4, label: '4 weeks' },
  { weeks: 8, label: '8 weeks' },
  { weeks: 13, label: '3 months' },
  { weeks: 26, label: '6 months' },
  { weeks: 52, label: '1 year' },
] as const

export function RangeChips({ value, onChange }: { value: number; onChange: (weeks: number) => void }) {
  return (
    <div className="tab-scroll" style={{ marginBottom: 'var(--sp-4)' }}>
      {CHART_RANGES.map((r) => (
        <button
          key={r.weeks}
          type="button"
          className={`btn btn--sm ${value === r.weeks ? 'btn--primary' : 'btn--secondary'}`}
          onClick={() => onChange(r.weeks)}
        >
          {r.label}
        </button>
      ))}
    </div>
  )
}

/** Cumulative growth curve ("total X over time") - a line with a soft area
 * fill beneath it, endpoint marked and direct-labelled with the current
 * total. Same frame conventions as WeeklyLineChart. */
export function CumulativeAreaChart({ weeks, values, label }: { weeks: string[]; values: number[]; label: string }) {
  const W = 560; const H = 180
  const padL = 8; const padR = 54; const padT = 10; const padB = 22
  const iw = W - padL - padR; const ih = H - padT - padB
  const max = scaleMax(values)
  const x = (i: number) => padL + (weeks.length < 2 ? iw / 2 : (i / (weeks.length - 1)) * iw)
  const y = (v: number) => padT + ih - (v / max) * ih
  const pts = values.map((v, i) => `${x(i)},${y(v)}`).join(' ')
  const area = `${padL},${padT + ih} ${pts} ${x(values.length - 1)},${padT + ih}`
  const last = values[values.length - 1] ?? 0

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label={label}>
      {[0.5].map((f) => (
        <line key={f} x1={padL} x2={W - padR} y1={padT + ih * f} y2={padT + ih * f}
              stroke="var(--border)" strokeWidth="1" strokeDasharray="2 4" />
      ))}
      <line x1={padL} x2={W - padR} y1={padT + ih} y2={padT + ih} stroke="var(--border)" strokeWidth="1" />
      <polygon points={area} fill="var(--accent-soft)" />
      <polyline points={pts} fill="none" stroke={ACCENT} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(values.length - 1)} cy={y(last)} r="4" fill={ACCENT} />
      <text x={x(values.length - 1) + 8} y={y(last) + 4}
            fontSize="12" fontWeight="700" fill="var(--text)">{last}</text>
      {weeks.map((w, i) => (
        (i === 0 || i === weeks.length - 1 || i === Math.floor((weeks.length - 1) / 2)) && (
          <text key={w} x={x(i)} y={H - 6} fontSize="11" textAnchor="middle" fill="var(--text-muted)">
            {weekTick(w)}
          </text>
        )
      ))}
    </svg>
  )
}
